import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity as ActivityIcon, Search, Phone, Mail, Users as UsersIcon, FileText, Pencil, Calendar } from 'lucide-react';
import api from '../api';
import { Activity, PaginatedResult } from '../types';
import Pagination from '../components/Pagination';

const formatDate = (dateStr: string): string =>
  new Date(dateStr).toLocaleDateString('fr-FR');

const activityTypeLabels: Record<string, string> = {
  email: 'Email',
  call: 'Appel',
  meeting: 'Réunion',
  letter: 'Courrier',
  note: 'Note',
};

const activityTypeIcons: Record<string, React.ReactNode> = {
  email: <Mail size={14} />,
  call: <Phone size={14} />,
  meeting: <UsersIcon size={14} />,
  letter: <FileText size={14} />,
  note: <Pencil size={14} />,
};

const activityBadgeColors: Record<string, string> = {
  email: 'badge-info',
  call: 'badge-success',
  meeting: 'badge-purple',
  letter: 'badge-warning',
  note: 'badge-gray',
};

const ActivityListPage: React.FC = () => {
  const navigate = useNavigate();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [typeFilter, setTypeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');

  const fetchActivities = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params: any = { page, limit };
      if (typeFilter) params.type = typeFilter;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (search) params.search = search;

      const res = await api.get<PaginatedResult<Activity>>('/activities', { params });
      setActivities(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      setError('Erreur lors du chargement des activités.');
    } finally {
      setLoading(false);
    }
  }, [page, limit, typeFilter, dateFrom, dateTo, search]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchActivities();
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div>
      <div className="page-header">
        <h1>Activités</h1>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="toolbar">
            <div className="toolbar-left">
              <form onSubmit={handleSearch} className="search-bar">
                <Search />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </form>
            </div>
          </div>

          <div className="filter-bar">
            <select
              className="form-select"
              value={typeFilter}
              onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
            >
              <option value="">Tous les types</option>
              <option value="call">Appel</option>
              <option value="email">Email</option>
              <option value="meeting">Réunion</option>
              <option value="letter">Courrier</option>
              <option value="note">Note</option>
            </select>

            <div className="inline-flex">
              <Calendar size={16} />
              <input
                type="date"
                className="form-input"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                style={{ width: '150px' }}
              />
              <span className="text-secondary">-</span>
              <input
                type="date"
                className="form-input"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                style={{ width: '150px' }}
              />
            </div>
          </div>

          {loading ? (
            <div className="spinner-container"><div className="spinner" /></div>
          ) : error ? (
            <div className="empty-state"><p className="empty-state-title">{error}</p></div>
          ) : activities.length === 0 ? (
            <div className="empty-state">
              <ActivityIcon size={48} />
              <p className="empty-state-title">Aucune activité trouvée</p>
              <p className="empty-state-description">Modifiez vos filtres ou ajoutez des activités depuis une fiche entreprise ou affaire.</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Sujet</th>
                    <th>Date</th>
                    <th>Entreprise</th>
                    <th>Contact</th>
                    <th>Affaire</th>
                    <th>Utilisateur</th>
                  </tr>
                </thead>
                <tbody>
                  {activities.map((a) => (
                    <tr key={a.id}>
                      <td>
                        <span className={`badge ${activityBadgeColors[a.type] || 'badge-gray'}`}>
                          {activityTypeIcons[a.type]} {activityTypeLabels[a.type] || a.type}
                        </span>
                      </td>
                      <td className="font-semibold">{a.subject}</td>
                      <td>{formatDate(a.activityDate)}</td>
                      <td>
                        {a.company ? (
                          <span
                            className="text-primary cursor-pointer"
                            onClick={() => navigate(`/companies/${a.company!.id}`)}
                          >
                            {a.company.name}
                          </span>
                        ) : '-'}
                      </td>
                      <td>
                        {a.contact ? (
                          <span
                            className="text-primary cursor-pointer"
                            onClick={() => navigate(`/contacts/${a.contact!.id}`)}
                          >
                            {a.contact.firstName} {a.contact.lastName}
                          </span>
                        ) : '-'}
                      </td>
                      <td>
                        {a.deal ? (
                          <span
                            className="text-primary cursor-pointer"
                            onClick={() => navigate(`/deals/${a.deal!.id}`)}
                          >
                            {a.deal.title}
                          </span>
                        ) : '-'}
                      </td>
                      <td>
                        {a.user ? `${a.user.firstName} ${a.user.lastName}` : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} total={total} />
        </div>
      </div>
    </div>
  );
};

export default ActivityListPage;
